// External Dependencies
import { Formik, Form } from 'formik';
import Box from '@mui/material/Box';
import styled from 'styled-components';

// Internal Dependencies
import {
  CustomFormButton,
  CustomInput,
  ErrorMessage,
} from './shared';
import {
  validateEmail,
  validatePasswordLength,
} from '../utils';

// Local Typings
interface Props {
  loginError: string;
  onSubmit: (values: any) => void;
}

// Local Variables
const StyledForm = styled(Form)(({ theme }) => ({
  '.custom-button': {
    margin: 10,
    padding: '6px 48px',
  },
  boxSizing: 'border-box',
  maxWidth: 368,
  padding: theme.spacing(3, 0),
}));

// Component Definition
const LoginForm = ({
  loginError,
  onSubmit,
}: Props): JSX.Element => {
  const validate = (values: any) => {
    const errors: any = {};

    if (validateEmail(values.email)) {
      errors.email = 'Invalid email';
    }

    if (validatePasswordLength(values.password)) {
      errors.password = 'Password must be at least 6 characters long';
    }

    // map over values and make all required
    Object.entries(values).forEach(([k, v]) => {
      if (!v) {
        errors[k] = 'Required';
      }
    });

    return errors;
  };

  return (
    <Formik
      initialValues={{
        email: '',
        password: '',
      }}
      onSubmit={async (values, { setSubmitting }) => {
        await onSubmit(values);
        setSubmitting(false);
      }}
      validate={validate}
    >
      {({ isSubmitting }) => (
        <StyledForm>
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            maxWidth={360}
            pb={4}
          >
            <CustomInput
              fullWidth
              label="Email"
              name="email"
              type="email"
            />
            <CustomInput
              fullWidth
              label="Password"
              name="password"
              type="password"
            />
          </Box>

          <CustomFormButton
            buttonText="Sign in"
            className="custom-button"
            disabled={isSubmitting}
          />

          <ErrorMessage
            condition={Boolean(loginError)}
            message={loginError}
          />
        </StyledForm>
      )}
    </Formik>
  );
};

export default LoginForm;
